import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'basic' | 'admin';

const variantConfig: Record<Variant, {
  headerBg: string;
  iconBg: string;
  badgeBg: string;
  badgeText: string;
}> = {
  basic: {
    headerBg: 'bg-sky-50 border-sky-100 dark:bg-slate-900/60 dark:border-slate-800',
    iconBg: 'bg-sky-500/15 text-sky-600 dark:bg-sky-500/25 dark:text-sky-200',
    badgeBg: 'bg-sky-100 dark:bg-sky-900/40',
    badgeText: 'text-sky-800 dark:text-sky-100'
  },
  admin: {
    headerBg: 'bg-rose-50 border-rose-100 dark:bg-rose-950/40 dark:border-rose-900/60',
    iconBg: 'bg-rose-500/15 text-rose-600 dark:bg-rose-500/25 dark:text-rose-200',
    badgeBg: 'bg-rose-100 dark:bg-rose-900/40',
    badgeText: 'text-rose-800 dark:text-rose-100'
  }
};

interface SettingsLayoutProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  badge?: string;
  actions?: ReactNode;
  variant?: Variant;
  children: ReactNode;
}

export function SettingsLayout({
  title,
  subtitle,
  icon,
  badge,
  actions,
  variant = 'basic',
  children
}: SettingsLayoutProps) {
  const config = variantConfig[variant];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className={cn('border-b', config.headerBg)}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={cn('p-3 rounded-xl', config.iconBg)}>
              {icon}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-foreground">{title}</h1>
                {badge && (
                  <span className={cn('text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide', config.badgeBg, config.badgeText)}>
                    {badge}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground max-w-2xl">{subtitle}</p>
            </div>
          </div>
          {actions && (
            <div className="flex items-center gap-3">{actions}</div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        {children}
      </main>
    </div>
  );
}
